<template>
  <div class="space-y-4">
    <!-- Chart header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <SymbolSelector
          v-model="selectedSymbol"
          @update:model-value="onSymbolChange"
          :disabled="isLoading"
        />
        <DataFreshnessIndicator
          v-if="chartData"
          :is-cached="chartData.metadata.is_cached"
          :timestamp="chartData.metadata.timestamp"
          :age-seconds="chartData.metadata.age_seconds"
        />
        <ThemeIndicator />
        <div class="flex items-center space-x-2 border-l pl-4 ml-2 border-gray-200 dark:border-gray-700">
           <label class="flex items-center space-x-2 text-sm cursor-pointer select-none">
             <input 
               type="checkbox" 
               v-model="showExtendedHours"
               class="form-checkbox h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary"
             >
             <span class="text-muted-foreground font-medium">Ext. Hours</span>
           </label>
        </div>
      </div>
      <div class="flex items-center space-x-2">
        <button
          @click="refreshChart"
          :disabled="isLoading"
          class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-8 w-8"
        >
          <svg
            v-if="isLoading"
            class="h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <svg
            v-else
            class="h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
            <path d="M3 21v-5h5" />
          </svg>
        </button>
        <button
          @click="$emit('remove')"
          class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-destructive text-destructive-foreground hover:bg-destructive/90 h-8 w-8"
        >
          <svg
            class="h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Drawing Tools and Indicators Toolbar -->
    <div class="flex flex-wrap items-center gap-4 p-2 bg-muted/30 border rounded-lg overflow-x-auto">
      <!-- Drawing Tools -->
      <div class="flex items-center gap-1 pr-4 border-r border-border/50">
        <span class="text-xs font-semibold text-muted-foreground mr-2 uppercase tracking-wider">Drawing</span>
        <button
          v-for="mode in ['trendline', 'horizontal', 'rectangle', 'fibonacci'] as const"
          :key="mode"
          @click="setDrawingMode(mode)"
          :title="mode.charAt(0).toUpperCase() + mode.slice(1)"
          :class="[
            'px-2 py-1.5 text-xs rounded-md transition-all duration-200 border',
            drawingMode === mode
              ? 'bg-primary text-primary-foreground border-primary shadow-sm'
              : 'bg-background hover:bg-muted text-muted-foreground border-transparent hover:border-border',
          ]"
        >
          <span v-if="mode === 'trendline'">📈</span>
          <span v-else-if="mode === 'horizontal'">➖</span>
          <span v-else-if="mode === 'rectangle'">⬜</span>
          <span v-else-if="mode === 'fibonacci'">📊</span>
        </button>
        <button
          @click="setDrawingMode('none')"
          title="Select Mode"
          :class="[
            'px-2 py-1.5 text-xs rounded-md transition-all duration-200 border ml-1',
            drawingMode === 'none'
              ? 'bg-primary text-primary-foreground border-primary shadow-sm'
              : 'bg-background hover:bg-muted text-muted-foreground border-transparent hover:border-border',
          ]"
        >
          ✋
        </button>
      </div>

      <!-- Indicators -->
      <div class="flex items-center gap-1 pr-4 border-r border-border/50">
        <span class="text-xs font-semibold text-muted-foreground mr-2 uppercase tracking-wider">Indicators</span>
        <button
          v-for="ind in ['sma', 'ema', 'rsi', 'macd']"
          :key="ind"
          @click="toggleIndicator(ind)"
          :title="ind.toUpperCase()"
          :class="[
            'px-2 py-1.5 text-xs rounded-md transition-all duration-200 border font-medium',
            activeIndicators.includes(ind)
              ? 'bg-primary text-primary-foreground border-primary shadow-sm'
              : 'bg-background hover:bg-muted text-muted-foreground border-transparent hover:border-border',
          ]"
        >
          {{ ind.toUpperCase() }}
        </button>
      </div>

      <!-- Projections -->
      <div class="flex items-center gap-1">
        <span class="text-xs font-semibold text-muted-foreground mr-2 uppercase tracking-wider">Projections</span>
        <button
          @click="addProjection('support')"
          title="Add Support Level"
          class="px-2 py-1.5 text-xs rounded-md bg-background border border-transparent hover:border-border hover:bg-muted text-muted-foreground transition-all duration-200"
        >
          🟢 Support
        </button>
        <button
          @click="addProjection('resistance')"
          title="Add Resistance Level"
          class="px-2 py-1.5 text-xs rounded-md bg-background border border-transparent hover:border-border hover:bg-muted text-muted-foreground transition-all duration-200"
        >
          🔴 Resistance
        </button>
        <button
          @click="addProjection('target')"
          title="Add Target Price"
          class="px-2 py-1.5 text-xs rounded-md bg-background border border-transparent hover:border-border hover:bg-muted text-muted-foreground transition-all duration-200"
        >
          🎯 Target
        </button>
      </div>

      <div class="flex-1"></div>

      <!-- Clear All -->
      <button
        @click="clearAllDrawings"
        title="Clear all drawings and indicators"
        class="px-2 py-1.5 text-xs rounded-md bg-destructive/10 text-destructive border border-transparent hover:bg-destructive/20 transition-all duration-200 flex items-center gap-1"
      >
        <span>🗑️</span> <span class="hidden sm:inline">Clear</span>
      </button>
    </div>

    <!-- Chart container -->
    <div class="relative">
      <div
        ref="chartContainer"
        class="h-96 w-full rounded-md border"
        :class="{ 'opacity-50': isLoading }"
      ></div>

      <!-- Loading state -->
      <div
        v-if="isLoading"
        class="absolute inset-0 flex items-center justify-center rounded-md bg-background/50 backdrop-blur-sm z-10"
      >
        <div class="flex flex-col items-center">
          <svg
            class="h-8 w-8 animate-spin text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span class="mt-2 text-sm font-medium text-muted-foreground">Loading chart data...</span>
        </div>
      </div>

      <!-- Error state -->
      <div
        v-if="error"
        class="absolute inset-0 flex items-center justify-center rounded-md bg-background/80 backdrop-blur-sm z-20"
      >
        <div class="text-center">
          <svg
            class="mx-auto h-10 w-10 text-destructive mb-2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
              clip-rule="evenodd"
            />
          </svg>
          <h3 class="text-lg font-medium text-destructive">Failed to load chart</h3>
          <p class="mt-1 text-sm text-muted-foreground max-w-xs mx-auto">{{ error }}</p>
          <button
            @click="refreshChart"
            class="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
          >
            Retry Loading
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import ApexCharts from 'apexcharts'
import ApexStock from 'apexstock'
import { apiClient, type ChartData } from '@/lib/api'
import { watchSystemTheme, getThemeColors, type Theme } from '@/lib/theme'
import SymbolSelector from './SymbolSelector.vue'
import DataFreshnessIndicator from './DataFreshnessIndicator.vue'
import ThemeIndicator from './ThemeIndicator.vue'

interface Props {
  symbol?: string
}

const props = withDefaults(defineProps<Props>(), {
  symbol: 'AAPL'
})

const emit = defineEmits<{
  remove: []
}>()

const chartContainer = ref<HTMLDivElement>()
const selectedSymbol = ref(props.symbol)
const chartData = ref<ChartData | null>(null)
const isLoading = ref(false)
const error = ref<string | null>(null)
const showExtendedHours = ref(false)
let pollInterval: ReturnType<typeof setInterval> | null = null

watch(showExtendedHours, () => {
  updateChartData()
})

// Drawing and indicators state
const drawingMode = ref<'none' | 'trendline' | 'horizontal' | 'rectangle' | 'fibonacci'>('none')
const activeIndicators = ref<string[]>([])
const annotations = ref<any[]>([])
const indicatorSeries = ref<Map<string, any>>(new Map())

// Theme state - default to dark mode
const currentTheme = ref<Theme>('dark')
let themeUnwatch: (() => void) | null = null

let stockChart: ApexStock | null = null

const createChartInstance = (data: any[] = []) => {
  if (!chartContainer.value) return

  try {
    // Ensure ApexCharts is available globally (required by ApexStock)
    if (typeof window !== 'undefined' && !(window as any).ApexCharts) {
      (window as any).ApexCharts = ApexCharts
    }

    const themeColorsObj = getThemeColors()
    const isDark = currentTheme.value === 'dark'

    // Enhanced chart options with zoom, pan, and dark mode support
    const chartOptions = {
      chart: {
        height: 384,
        background: isDark ? '#1a1a1a' : '#ffffff', // Dark background in dark mode, white in light mode
        toolbar: {
          show: true,
          autoSelected: 'pan', // Default to pan mode
          tools: {
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
            download: false, // Disable download button (ApexStock has its own export)
          },
        },
        zoom: {
          enabled: true,
          type: 'x', // Zoom on x-axis only
          autoScaleYaxis: true, // Auto-scale y-axis when zooming
          allowMouseWheelZoom: true, // Enable mouse wheel zoom
        },
        pan: {
          enabled: true,
          type: 'x', // Pan on x-axis only
        },
        selection: {
          enabled: true,
          type: 'x', // Selection for zoom
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
      },
      series: [
        {
          data: data, // Data format: { x: dateString, y: [open, high, low, close], v: volume }
        },
      ],
      xaxis: {
        type: 'category',
        min: undefined, // Allow panning beyond data range
        max: undefined, // Allow panning beyond data range
        labels: {
          style: {
            colors: isDark ? '#e0e0e0' : '#333',
            fontSize: '12px',
          },
        },
        axisBorder: {
          color: isDark ? '#404040' : '#e9ecef',
        },
        axisTicks: {
          color: isDark ? '#404040' : '#e9ecef',
        },
      },
      yaxis: {
        tooltip: {
          enabled: true,
        },
        labels: {
          style: {
            colors: isDark ? '#e0e0e0' : '#333',
            fontSize: '12px',
          },
          formatter: (val: number) => val.toFixed(2),
        },
        opposite: true, // Show y-axis on the right side
      },
      grid: {
        borderColor: isDark ? '#404040' : '#e9ecef',
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: false, // Hide vertical grid lines
          },
        },
        yaxis: {
          lines: {
            show: true, // Keep horizontal grid lines
          },
        },
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
      },
      tooltip: {
        theme: currentTheme.value,
        style: {
          fontSize: '12px',
          background: isDark ? '#2a2a2a' : '#ffffff',
          color: isDark ? '#e0e0e0' : '#333',
          border: isDark ? '1px solid #404040' : '1px solid #e9ecef',
        },
        x: {
          format: 'MMM dd, yyyy',
        },
        y: {
          formatter: (val: number) => `$${val.toFixed(2)}`,
        },
        // Use default tooltip which handles OHLC data automatically
        // Custom tooltip was causing errors during pan/zoom operations
      },
      theme: {
        mode: currentTheme.value,
      },
      plotOptions: {
        candlestick: {
          colors: {
            upward: themeColorsObj.bullish,
            downward: themeColorsObj.bearish,
          },
          wick: {
            useFillColor: true,
          },
        },
      },
      // Enhanced dark mode colors
      colors: isDark
        ? ['#26A69A', '#EF5350'] // Teal for up, red for down in dark mode
        : ['#00B746', '#EF403C'], // Green for up, red for down in light mode
      legend: {
        show: true,
        labels: {
          colors: isDark ? '#e0e0e0' : '#333', // Dark text in dark mode, dark text in light mode
        },
        markers: {
          fillColors: isDark
            ? ['#26A69A', '#EF5350']
            : ['#00B746', '#EF403C'],
        },
      },
    }

    console.log('Creating ApexStock with options:', {
      chart: chartOptions.chart,
      seriesCount: chartOptions.series.length,
      firstSeriesDataLength: chartOptions.series[0].data.length,
      firstDataPoint: chartOptions.series[0].data[0],
      theme: chartOptions.theme
    })

    stockChart = new ApexStock(chartContainer.value, chartOptions)
    console.log('ApexStock instance created:', stockChart)
    stockChart.render()
    console.log('Chart rendered')

    // Configure panning to allow movement outside data range
    setTimeout(() => {
      if (stockChart && (stockChart as any).chart) {
        const chartInstance = (stockChart as any).chart
        // Remove x-axis min/max constraints to allow panning outside data range
        if (chartInstance.w && chartInstance.w.config) {
          // Update through ApexStock's update method
          stockChart.update({
            xaxis: {
              min: undefined,
              max: undefined,
            },
            chart: {
              pan: {
                enabled: true,
                type: 'x',
              },
            },
          })

          // Also update the underlying chart instance directly
          chartInstance.w.config.xaxis.min = undefined
          chartInstance.w.config.xaxis.max = undefined
          chartInstance.w.config.chart.pan = {
            enabled: true,
            type: 'x',
          }
        }
      }
    }, 200)

    // Check if data was actually set after render
    setTimeout(() => {
      if (stockChart && (stockChart as any).chart) {
        const chartInstance = (stockChart as any).chart
        const seriesConfig = chartInstance.w?.config?.series?.[0]
        const chartConfig = chartInstance.w?.config?.chart

        console.log('=== DEBUGGING CHART CONFIGURATION ===')
        console.log('Chart type:', chartConfig?.type)
        console.log('Series type:', seriesConfig?.type)
        console.log('Series name:', seriesConfig?.name)
        console.log('Data length:', seriesConfig?.data?.length)
        console.log('First data point:', seriesConfig?.data?.[0])
        console.log('Data point keys:', seriesConfig?.data?.[0] ? Object.keys(seriesConfig?.data[0]) : 'no data')

        // Check if data has OHLC format (y array format: [open, high, low, close])
        const firstPoint = seriesConfig?.data?.[0]
        if (firstPoint) {
          console.log('Has y property:', 'y' in firstPoint)
          console.log('Has x property:', 'x' in firstPoint)
          console.log('Has v property:', 'v' in firstPoint)
          console.log('Y is array:', Array.isArray(firstPoint.y))
          console.log('Y array length:', firstPoint.y?.length)
          console.log('Y array values:', firstPoint.y)
          console.log('X value type:', typeof firstPoint.x)
          console.log('X value:', firstPoint.x)
        }

        // Calculate actual price range from y array format
        if (seriesConfig?.data?.length > 0) {
          const allPrices = seriesConfig.data.flatMap((d: any) => {
            if (Array.isArray(d.y) && d.y.length === 4) {
              return d.y // [open, high, low, close]
            }
            // Fallback for o, h, l, c format
            return [d.o, d.h, d.l, d.c].filter((v: any) => typeof v === 'number')
          }).filter((v: any) => typeof v === 'number')
          const minPrice = Math.min(...allPrices)
          const maxPrice = Math.max(...allPrices)
          console.log('Price range:', { min: minPrice, max: maxPrice })

          // Check y-axis range
          const yaxis = chartInstance.w?.config?.yaxis?.[0]
          console.log('Y-axis min:', yaxis?.min)
          console.log('Y-axis max:', yaxis?.max)
          console.log('Y-axis autoScale:', yaxis?.forceNiceScale)
        }

        console.log('=== END DEBUGGING ===')
      }
    }, 1000)
  } catch (err: any) {
    console.error('Error creating chart:', err)
    error.value = err.message || 'Failed to create chart'
  }
}

const convertToApexStockFormat = (data: ChartData['data']) => {
  if (!data || data.length === 0) {
    console.warn('No data to convert')
    return []
  }

  // ApexStock expects data in format: { x: date, y: [open, high, low, close], v: volume }
  // Following the official ApexStock example format
  const converted = data
    .map((point) => ({
      x: new Date(point.time).toString(), // Convert to string format like the example
      y: [
        Number(point.open.toFixed(2)),
        Number(point.high.toFixed(2)),
        Number(point.low.toFixed(2)),
        Number(point.close.toFixed(2)),
      ],
      v: point.volume || 0,
    }))
    .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()) // Sort by date ascending

  // Add padding data points before and after to allow panning beyond data range
  const paddingDays = 60 // Add 60 days of padding on each side
  const beforePadding: any[] = []
  const afterPadding: any[] = []
  
  if (converted.length > 0) {
    const firstDate = new Date(converted[0].x)
    const lastDate = new Date(converted[converted.length - 1].x)
    
    // Calculate average price for padding points (use first/last close price)
    const firstClose = converted[0].y[3] // Close price
    const lastClose = converted[converted.length - 1].y[3] // Close price
    
    // Add padding points before the first data point
    for (let i = paddingDays; i > 0; i--) {
      const paddingDate = new Date(firstDate)
      paddingDate.setDate(paddingDate.getDate() - i)
      
      // Use same price for all OHLC to create invisible padding points (no visible candlestick)
      beforePadding.push({
        x: paddingDate.toString(),
        y: [firstClose, firstClose, firstClose, firstClose],
        v: 0, // No volume
      })
    }
    
    // Add padding points after the last data point
    for (let i = 1; i <= paddingDays; i++) {
      const paddingDate = new Date(lastDate)
      paddingDate.setDate(paddingDate.getDate() + i)
      
      afterPadding.push({
        x: paddingDate.toString(),
        y: [lastClose, lastClose, lastClose, lastClose],
        v: 0, // No volume
      })
    }
  }
  
  // Combine: before padding + actual data + after padding
  const result = [...beforePadding, ...converted, ...afterPadding]

  console.log('Converted data with padding:', {
    originalCount: data.length,
    convertedCount: converted.length,
    beforePaddingCount: beforePadding.length,
    afterPaddingCount: afterPadding.length,
    totalCount: result.length,
    firstXValue: result[0]?.x,
    lastXValue: result[result.length - 1]?.x,
    priceRange: {
      min: Math.min(...converted.flatMap(d => d.y)),
      max: Math.max(...converted.flatMap(d => d.y))
    }
  })

  return result
}

const updateChartData = async () => {
  if (!selectedSymbol.value) {
    error.value = 'No symbol selected'
    return
  }

  // Don't require stockChart to be created yet - we'll create it if needed
  isLoading.value = true
  error.value = null

  try {
    // Start background job
    const jobResponse = await apiClient.startChartJob(selectedSymbol.value, '1y', '1d', showExtendedHours.value)

    // Poll for completion
    await pollJobStatus(jobResponse.job_id)
  } catch (err: any) {
    console.error('Error starting chart job:', err)
    error.value = err.response?.data?.detail || err.message || 'Failed to load chart data'
    chartData.value = null
    isLoading.value = false
  }
}

const pollJobStatus = async (jobId: string) => {
  if (pollInterval) {
    clearInterval(pollInterval)
  }

  let pollCount = 0
  const maxPolls = 60 // Maximum 60 seconds of polling
  
  pollInterval = setInterval(async () => {
    pollCount++

    // Timeout after max polls
    if (pollCount > maxPolls) {
      if (pollInterval) {
        clearInterval(pollInterval)
        pollInterval = null
      }
      error.value = 'Chart data job timed out'
      isLoading.value = false
      return
    }

    try {
      const status = await apiClient.getChartJobStatus(jobId)
      
      if (status.status === 'completed' && status.result) {
        chartData.value = status.result
        
        // Convert data to ApexStock format
        const chartDataPoints = convertToApexStockFormat(status.result.data)

        console.log('Updating chart with data:', {
          symbol: selectedSymbol.value,
          dataPointsCount: chartDataPoints.length,
          firstPoint: chartDataPoints[0],
          lastPoint: chartDataPoints[chartDataPoints.length - 1],
          samplePoints: chartDataPoints.slice(0, 5)
        })

        // Create or update chart with data
        if (!stockChart && chartContainer.value) {
          console.log('Creating new chart with data')
          // Create chart with data
          createChartInstance(chartDataPoints)
          console.log('Chart created, stockChart:', stockChart)
        } else if (stockChart) {
          console.log('Updating existing chart')
          // Destroy and recreate chart to ensure data is properly loaded
          stockChart.destroy()
          stockChart = null
          await new Promise(resolve => setTimeout(resolve, 100))
          createChartInstance(chartDataPoints)
          console.log('Chart recreated with data')
        } else {
          console.warn('Cannot create/update chart: stockChart=', stockChart, 'chartContainer=', chartContainer.value)
        }
        
        isLoading.value = false
        if (pollInterval) {
          clearInterval(pollInterval)
          pollInterval = null
        }
      } else if (status.status === 'failed') {
        error.value = status.error || 'Chart data job failed'
        isLoading.value = false
        chartData.value = null
        if (pollInterval) {
          clearInterval(pollInterval)
          pollInterval = null
        }
      }
      // If status is still 'pending' or 'running', continue polling
    } catch (err: any) {
      console.error('Error polling chart job status:', err)
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        jobId
      })

      if (pollInterval) {
        clearInterval(pollInterval)
        pollInterval = null
      }
      isLoading.value = false
      error.value = err.response?.data?.detail || err.message || `Failed to get job status: ${err.response?.status || 'Unknown error'}`
      chartData.value = null
    }
  }, 1000) // Poll every second for quick jobs
}

const onSymbolChange = (symbol: string) => {
  selectedSymbol.value = symbol
  updateChartData()
}

const refreshChart = () => {
  updateChartData()
}

const updateChartTheme = () => {
  if (!stockChart) return

  const themeColorsObj = getThemeColors()
  const isDark = currentTheme.value === 'dark'

  stockChart.update({
    chart: {
      background: isDark ? '#1a1a1a' : '#ffffff', // Update background color
    },
    theme: {
      mode: currentTheme.value,
    },
    xaxis: {
      min: undefined, // Allow panning beyond data range
      max: undefined, // Allow panning beyond data range
      labels: {
        style: {
          colors: isDark ? '#e0e0e0' : '#333',
        },
      },
      axisBorder: {
        color: isDark ? '#404040' : '#e9ecef',
      },
      axisTicks: {
        color: isDark ? '#404040' : '#e9ecef',
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: isDark ? '#e0e0e0' : '#333',
        },
      },
    },
    grid: {
      borderColor: isDark ? '#404040' : '#e9ecef',
      xaxis: {
        lines: {
          show: false, // Hide vertical grid lines
        },
      },
      yaxis: {
        lines: {
          show: true, // Keep horizontal grid lines
        },
      },
    },
    tooltip: {
      theme: currentTheme.value,
      style: {
        background: isDark ? '#2a2a2a' : '#ffffff',
        color: isDark ? '#e0e0e0' : '#333',
        border: isDark ? '1px solid #404040' : '1px solid #e9ecef',
      },
    },
    plotOptions: {
      candlestick: {
        colors: {
          upward: themeColorsObj.bullish,
          downward: themeColorsObj.bearish,
        },
      },
    },
    colors: isDark
      ? ['#26A69A', '#EF5350'] // Teal for up, red for down in dark mode
      : ['#00B746', '#EF403C'], // Green for up, red for down in light mode
    legend: {
      labels: {
        colors: isDark ? '#e0e0e0' : '#333', // Light text in dark mode, dark text in light mode
      },
      markers: {
        fillColors: isDark
          ? ['#26A69A', '#EF5350']
          : ['#00B746', '#EF403C'],
      },
    },
  })
}

const handleResize = () => {
  if (stockChart && chartContainer.value) {
    stockChart.update({
      chart: {
        height: 384,
      },
    })
  }
}

// Drawing and indicator functions
const setDrawingMode = (mode: typeof drawingMode.value) => {
  drawingMode.value = mode
  // ApexStock handles drawing tools through its built-in toolbar
}

const toggleIndicator = async (indicator: string) => {
  if (!chartData.value || !stockChart) return

  const index = activeIndicators.value.indexOf(indicator)
  if (index > -1) {
    // Remove indicator
    activeIndicators.value.splice(index, 1)
      indicatorSeries.value.delete(indicator)
    updateChartWithIndicators()
  } else {
    // Add indicator
    activeIndicators.value.push(indicator)
    await addIndicator(indicator)
  }
}

const addIndicator = async (indicator: string) => {
  if (!chartData.value || !stockChart) return

  const data = chartData.value.data
  let indicatorData: { x: number; y: number }[] = []

  switch (indicator) {
    case 'sma':
      indicatorData = calculateSMA(data).map((point) => ({
        x: new Date(point.time).getTime(),
        y: point.value,
      }))
      break
    case 'ema':
      indicatorData = calculateEMA(data).map((point) => ({
        x: new Date(point.time).getTime(),
        y: point.value,
      }))
      break
    case 'rsi':
      indicatorData = calculateRSI(data).map((point) => ({
        x: new Date(point.time).getTime(),
        y: point.value,
      }))
      break
    case 'macd':
      indicatorData = calculateMACD(data).map((point) => ({
        x: new Date(point.time).getTime(),
        y: point.value,
      }))
      break
  }

  if (indicatorData.length > 0) {
    indicatorSeries.value.set(indicator, indicatorData)
    updateChartWithIndicators()
  }
}

const updateChartWithIndicators = () => {
  if (!stockChart || !chartData.value) return

  const series: any[] = [
    {
      name: selectedSymbol.value,
      data: convertToApexStockFormat(chartData.value.data),
    },
  ]

  // Add indicator series
  activeIndicators.value.forEach((indicator: string) => {
    const indicatorData = indicatorSeries.value.get(indicator)
    if (indicatorData) {
      series.push({
        name: indicator.toUpperCase(),
        type: 'line',
        data: indicatorData,
      color: getIndicatorColor(indicator),
      })
    }
  })

  stockChart.update({
    series,
  })
}

const addProjection = (type: 'support' | 'resistance' | 'target') => {
  if (!chartData.value) return

  const data = chartData.value.data
  const lastPrice = data[data.length - 1]?.close || 0

  let price: number
  let color: string
  let label: string

  const themeColorsObj = getThemeColors()

  switch (type) {
    case 'support':
      price = lastPrice * 0.95 // 5% below current price
      color = themeColorsObj.projections.support
      label = 'Support'
      break
    case 'resistance':
      price = lastPrice * 1.05 // 5% above current price
      color = themeColorsObj.projections.resistance
      label = 'Resistance'
      break
    case 'target':
      price = lastPrice * 1.10 // 10% above current price
      color = themeColorsObj.projections.target
      label = 'Target'
      break
  }

  annotations.value.push({
    y: price,
    borderColor: color,
    label: {
      borderColor: color,
      style: {
        color: '#fff',
        background: color,
      },
      text: label,
    },
  })

  if (stockChart) {
    stockChart.update({
      annotations: {
        yaxis: annotations.value,
      },
    })
  }
}

const clearAllDrawings = () => {
  annotations.value = []
  indicatorSeries.value.clear()
  activeIndicators.value = []

  if (stockChart) {
    stockChart.update({
      annotations: {
        yaxis: [],
      },
    })
    updateChartWithIndicators()
  }

  // Reset drawing mode
  drawingMode.value = 'none'
}

// Technical indicator calculations
const calculateSMA = (data: ChartData['data'], period: number = 20) => {
  const result: { time: string; value: number }[] = []

  for (let i = period - 1; i < data.length; i++) {
    let sum = 0
    for (let j = 0; j < period; j++) {
      sum += data[i - j].close
    }
    result.push({
      time: data[i].time,
      value: sum / period
    })
  }

  return result
}

const calculateEMA = (data: ChartData['data'], period: number = 20) => {
  const result: { time: string; value: number }[] = []
  const multiplier = 2 / (period + 1)

  // Start with SMA for the first value
  let sum = 0
  for (let i = 0; i < period; i++) {
    sum += data[i].close
  }
  let ema = sum / period

  result.push({
    time: data[period - 1].time,
    value: ema
  })

  // Calculate EMA for remaining values
  for (let i = period; i < data.length; i++) {
    ema = (data[i].close * multiplier) + (ema * (1 - multiplier))
    result.push({
      time: data[i].time,
      value: ema
    })
  }

  return result
}

const calculateRSI = (data: ChartData['data'], period: number = 14) => {
  const result: { time: string; value: number }[] = []
  const gains: number[] = []
  const losses: number[] = []

  // Calculate price changes
  for (let i = 1; i < data.length; i++) {
    const change = data[i].close - data[i - 1].close
    gains.push(change > 0 ? change : 0)
    losses.push(change < 0 ? Math.abs(change) : 0)
  }

  // Calculate initial averages
  let avgGain = gains.slice(0, period).reduce((a, b) => a + b, 0) / period
  let avgLoss = losses.slice(0, period).reduce((a, b) => a + b, 0) / period

  for (let i = period; i < gains.length; i++) {
    avgGain = ((avgGain * (period - 1)) + gains[i]) / period
    avgLoss = ((avgLoss * (period - 1)) + losses[i]) / period

    const rs = avgGain / avgLoss
    const rsi = 100 - (100 / (1 + rs))

    result.push({
      time: data[i + 1].time,
      value: rsi
    })
  }

  return result
}

const calculateMACD = (data: ChartData['data']) => {
  const ema12 = calculateEMA(data, 12)
  const ema26 = calculateEMA(data, 26)
  const result: { time: string; value: number }[] = []

  const minLength = Math.min(ema12.length, ema26.length)

  for (let i = 0; i < minLength; i++) {
    const macd = ema12[i].value - ema26[i].value
    result.push({
      time: ema12[i].time,
      value: macd
    })
  }

  return result
}

const getIndicatorColor = (indicator: string): string => {
  const themeColorsObj = getThemeColors()
  return themeColorsObj.indicators[indicator as keyof typeof themeColorsObj.indicators] || '#6b7280'
}

onMounted(async () => {
  try {
    await nextTick()

    // Don't create chart immediately - wait for data
    // createChartInstance()

    await updateChartData()

    // Set up theme - default to dark mode (following example pattern)
    // Check if document has dark-mode class (like in the example)
    if (typeof document !== 'undefined') {
      const hasDarkClass = document.body.classList.contains('dark-mode') || 
                          document.documentElement.classList.contains('dark')
      if (hasDarkClass) {
        currentTheme.value = 'dark'
      } else {
        // Default to dark mode (as requested)
        currentTheme.value = 'dark'
      }
    } else {
      // Default to dark mode
      currentTheme.value = 'dark'
    }
    
    // Set up theme watching for changes (optional - can still respond to system changes)
    themeUnwatch = watchSystemTheme((_theme: Theme) => {
      // Keep dark mode as default - don't override with system preference
      // If you want system preference to override, uncomment: currentTheme.value = theme || 'dark'
      updateChartTheme()
    })
    
    // Initialize chart theme if chart already exists
    if (stockChart) {
      updateChartTheme()
    }

    // Add resize listener
    window.addEventListener('resize', handleResize)
  } catch (err) {
    console.error('Error initializing chart:', err)
    error.value = 'Failed to initialize chart'
  }
})

onUnmounted(() => {
  if (stockChart) {
    stockChart.destroy()
    stockChart = null
  }

  // Clean up theme watcher
  if (themeUnwatch) {
    themeUnwatch()
  }

  // Clean up polling interval
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }

  window.removeEventListener('resize', handleResize)
})

// Watch for symbol prop changes
watch(() => props.symbol, (newSymbol: string) => {
  if (newSymbol && newSymbol !== selectedSymbol.value) {
    selectedSymbol.value = newSymbol
    updateChartData()
  }
})
</script>

<style scoped>
/* Override ApexCharts tooltip styles for dark mode */
:deep(.apexcharts-tooltip) {
  background: var(--background) !important;
  color: var(--foreground) !important;
  border-color: hsl(var(--border)) !important;
}

:deep(.apexcharts-tooltip.apexcharts-theme-dark) {
  background: #2a2a2a !important;
  color: #e0e0e0 !important;
  border-color: #404040 !important;
}

:deep(.apexcharts-tooltip-box) {
  fill: var(--background) !important;
}

:deep(.apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-box) {
  fill: #2a2a2a !important;
}

:deep(.apexcharts-tooltip-text) {
  color: var(--foreground) !important;
}

:deep(.apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-text) {
  color: #e0e0e0 !important;
}
</style>
